/**
 * Client-side Firestore API for the `scanned_receipts` collection (M13a Belegscanner).
 *
 * Staging-Datenzugriffsschicht für die KI-OCR-Belege. Spiegelt das M0-Muster aus
 * `expensesApi.ts`. KEINE Steuer-/§14-Logik hier — die kommt in M13a-3/4
 * (`normalizeAmounts.ts` / `mapScannedReceiptToExpenseDraft.ts`).
 *
 * Owner-Scoping (M0-konform):
 *   - Jeder Write setzt `userId = auth.currentUser.uid` (ignoriert Caller-Wert).
 *   - Jede Read-Query trägt `where('userId', '==', uid)`.
 *   - Cross-User-Reads werden hier UND von den Firestore-Rules abgelehnt.
 *
 * GoBD-Härtung (W-1): die Extraktions-/Betrags-Felder werden im produktiven
 * Flow von der `extractReceipt`-Function (Admin-SDK) geschrieben und sind über
 * den Client unveränderbar. Die Firestore-Rule erlaubt per Client-UPDATE NUR
 * `hasOnly(['status', 'sourceExpenseId'])` — exakt die beiden Übergänge, die
 * {@link markScannedReceiptBooked} und {@link markScannedReceiptDiscarded}
 * vornehmen.
 *
 * Cent-Disziplin: die `total*Cents`-Felder und `normalizedBreakdown[].netCents/
 * vatCents` MÜSSEN Integer sein (oder null). {@link createScannedReceipt}
 * validiert das fail-closed (Golden Rule: keine Silent Fallbacks).
 *
 * Index-Anforderungen (müssen vor Produktiv-Nutzung existieren — siehe
 * `firestore/firestore.indexes.json`):
 *   - scanned_receipts: userId ASC, createdAt DESC            (getScannedReceiptsForUser)
 *   - expenses:         userId ASC, sourceScannedReceiptId ASC (getExpenseForScannedReceipt, Dedup S-1.6)
 *
 * @module scannedReceiptsApi
 */

import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { getEffectiveScopeUid } from '../utils/tenantScope';
import {
  ScannedReceipt,
  CreateScannedReceiptInput,
  NormalizedRateLine,
} from '../types/scannedReceiptTypes';
import { Expense } from '../types/expenseTypes';

const SCANNED_RECEIPTS_COLLECTION = 'scanned_receipts';
const EXPENSES_COLLECTION = 'expenses';

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Returns the effective scope UID for owner-scoped queries.
 *
 * Fail-closed: throws if no user is signed in (Golden Rule — never an anonymous
 * scope). The returned value is the *active tenant scope* via
 * {@link getEffectiveScopeUid}: bit-identical to `auth.currentUser.uid` while the
 * flag is off or the own tenant is active (M11 inertness), and the mandate's
 * `scopeUid` when an advisor has a foreign tenant active.
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error(
      'scannedReceiptsApi: No authenticated user. Sign in before accessing scanned receipts.',
    );
  }
  return getEffectiveScopeUid();
}

/**
 * Validates that every monetary cent field on a scanned receipt is an integer
 * (or null). The raw EUR-decimal extraction lives in `extraction`; ONLY the
 * server-normalized `total*Cents` + `normalizedBreakdown` carry cents and must
 * obey the Cent-Disziplin.
 *
 * Throws a descriptive error on the first violation found — fail-closed.
 */
function validateCentFields(
  input: Pick<
    CreateScannedReceiptInput,
    'totalNetCents' | 'totalVatCents' | 'totalGrossCents' | 'normalizedBreakdown'
  >,
): void {
  const topLevel: Array<[string, number | null]> = [
    ['totalNetCents', input.totalNetCents],
    ['totalVatCents', input.totalVatCents],
    ['totalGrossCents', input.totalGrossCents],
  ];

  for (const [name, value] of topLevel) {
    if (value !== null && !Number.isInteger(value)) {
      throw new Error(
        `scannedReceiptsApi: ${name} must be an integer or null (got ${value}). ` +
          'Server-normalized amounts are integer EUR cents.',
      );
    }
  }

  input.normalizedBreakdown.forEach((line: NormalizedRateLine, idx: number) => {
    const lineFields: Array<[string, number | null]> = [
      ['netCents', line.netCents],
      ['vatCents', line.vatCents],
    ];
    for (const [name, value] of lineFields) {
      if (value !== null && !Number.isInteger(value)) {
        throw new Error(
          `scannedReceiptsApi: normalizedBreakdown[${idx}].${name} must be an ` +
            `integer or null (got ${value}).`,
        );
      }
    }
  });
}

// ─── createScannedReceipt ─────────────────────────────────────────────────────

/**
 * Creates a new staging document in the `scanned_receipts` collection.
 *
 * NOTE: In the production flow the extraction document is written server-side by
 * the `extractReceipt` Cloud Function (Admin SDK) after it has read the image,
 * called Claude Vision and run the DE-normalization. This client-side creator
 * exists for parity/testability and enforces the same M0 + Cent guarantees.
 *
 * Enforces:
 *   - `userId` and `createdBy` are always set to `auth.currentUser.uid` (M0 scope).
 *   - `createdAt` is set via `serverTimestamp()`.
 *   - All cent fields must be integers or null (validateCentFields).
 *
 * @param input - Scanned-receipt fields. Cent fields must be integers or null.
 * @returns Firestore document ID of the newly created document.
 * @throws {Error} If not authenticated, cent validation fails, or the write fails.
 */
export async function createScannedReceipt(
  input: CreateScannedReceiptInput,
): Promise<string> {
  const uid = requireCurrentUser();

  validateCentFields(input);

  const docData: Omit<ScannedReceipt, 'id'> = {
    ...input,
    userId: uid, // always overwrite — M0 scope
    createdBy: uid,
    createdAt: serverTimestamp() as ScannedReceipt['createdAt'],
  };

  const colRef = collection(db, SCANNED_RECEIPTS_COLLECTION);
  const docRef = await addDoc(colRef, docData);
  return docRef.id;
}

// ─── getScannedReceiptsForUser ────────────────────────────────────────────────

/**
 * Loads all scanned-receipt documents for the given user, newest first.
 *
 * Requires a composite Firestore index: `userId ASC, createdAt DESC`.
 * Cross-user reads are rejected: `uid` must match `auth.currentUser.uid`.
 *
 * @param uid - UID whose receipts to load. Must match the authenticated user.
 * @returns Array of ScannedReceipt objects (may be empty).
 * @throws {Error} If not authenticated, uid mismatch, or the query fails.
 */
export async function getScannedReceiptsForUser(
  uid: string,
): Promise<ScannedReceipt[]> {
  const currentUid = requireCurrentUser();

  if (uid !== currentUid) {
    throw new Error(
      'scannedReceiptsApi.getScannedReceiptsForUser: uid must match the ' +
        'authenticated user. Cross-user reads are denied by Firestore Rules.',
    );
  }

  const q = query(
    collection(db, SCANNED_RECEIPTS_COLLECTION),
    where('userId', '==', uid),
    orderBy('createdAt', 'desc'),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (d): ScannedReceipt => ({ ...(d.data() as ScannedReceipt), id: d.id }),
  );
}

// ─── getScannedReceipt ────────────────────────────────────────────────────────

/**
 * Fetches a single scanned receipt by document ID, scoped to the current user.
 *
 * @param receiptId - Firestore document ID.
 * @returns The ScannedReceipt or null if not found / not owned by the current user.
 * @throws {Error} If not authenticated, the document belongs to another user, or the read fails.
 */
export async function getScannedReceipt(
  receiptId: string,
): Promise<ScannedReceipt | null> {
  const uid = requireCurrentUser();

  const docRef = doc(db, SCANNED_RECEIPTS_COLLECTION, receiptId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as ScannedReceipt;

  // Client-side ownership guard (belt + suspenders alongside Firestore Rules).
  if (data.userId !== uid) {
    throw new Error(
      'scannedReceiptsApi.getScannedReceipt: document belongs to a different ' +
        'user. Cross-user reads are denied.',
    );
  }

  return { ...data, id: snapshot.id };
}

// ─── markScannedReceiptBooked ─────────────────────────────────────────────────

/**
 * Transitions a scanned receipt to `status='booked'` and links it to the
 * created expense via `sourceExpenseId`.
 *
 * Only `status` and `sourceExpenseId` are written — this is the exact set the
 * Firestore Rule `hasOnly(['status', 'sourceExpenseId'])` permits for the
 * client (GoBD-Härtung W-1). Extraction/amount fields stay immutable.
 *
 * @param receiptId - Firestore document ID of the scanned receipt.
 * @param expenseId - Firestore document ID of the booked expense.
 * @throws {Error} If not authenticated or the update fails.
 */
export async function markScannedReceiptBooked(
  receiptId: string,
  expenseId: string,
): Promise<void> {
  requireCurrentUser();

  const docRef = doc(db, SCANNED_RECEIPTS_COLLECTION, receiptId);
  await updateDoc(docRef, {
    status: 'booked',
    sourceExpenseId: expenseId,
  } as Partial<ScannedReceipt>);
}

// ─── markScannedReceiptDiscarded ──────────────────────────────────────────────

/**
 * Transitions a scanned receipt to `status='discarded'` (final, non-booking).
 *
 * A discarded scan can never be booked afterwards (the modal path blocks it).
 * The image is NOT deleted here (write-once + S-2 lifecycle, §10/M13a-6).
 *
 * @param receiptId - Firestore document ID of the scanned receipt.
 * @throws {Error} If not authenticated or the update fails.
 */
export async function markScannedReceiptDiscarded(
  receiptId: string,
): Promise<void> {
  requireCurrentUser();

  const docRef = doc(db, SCANNED_RECEIPTS_COLLECTION, receiptId);
  await updateDoc(docRef, {
    status: 'discarded',
  } as Partial<ScannedReceipt>);
}

// ─── deleteScannedReceipt ─────────────────────────────────────────────────────

/**
 * Deletes a scanned-receipt staging document.
 *
 * Only the owner may delete (`allow delete: if isOwner(resource.data)`). Note:
 * deleting the Firestore doc does NOT delete the Storage image — the GoBD
 * retention / orphan-cleanup lifecycle (M13a-6) governs image removal.
 *
 * @param receiptId - Firestore document ID of the scanned receipt.
 * @throws {Error} If not authenticated or the delete fails.
 */
export async function deleteScannedReceipt(receiptId: string): Promise<void> {
  requireCurrentUser();

  const docRef = doc(db, SCANNED_RECEIPTS_COLLECTION, receiptId);
  await deleteDoc(docRef);
}

// ─── getExpenseForScannedReceipt (Dedup, S-1.6) ───────────────────────────────

/**
 * Looks up an existing expense by its source scanned-receipt ID, scoped to the
 * current user. Mirrors `getExpenseForReceivedInvoice` for the scan path: if an
 * expense already exists for a given `sourceScannedReceiptId`, the UI can warn
 * the user before booking the same receipt twice (Doppelbuchungs-Schutz).
 *
 * Requires a composite Firestore index: `expenses: userId ASC, sourceScannedReceiptId ASC`.
 *
 * @param scannedReceiptId - Firestore document ID from the `scanned_receipts` collection.
 * @returns The matching Expense, or null if none exists.
 * @throws {Error} If not authenticated or the query fails.
 */
export async function getExpenseForScannedReceipt(
  scannedReceiptId: string,
): Promise<Expense | null> {
  const uid = requireCurrentUser();

  const q = query(
    collection(db, EXPENSES_COLLECTION),
    where('userId', '==', uid),
    where('sourceScannedReceiptId', '==', scannedReceiptId),
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  const first = snapshot.docs[0];
  return { ...(first.data() as Expense), id: first.id };
}
